import {
  IAvailableSlas,
  IAvailableWindows
} from '@smuchile/pkg-unimarc-hooks/shared/interfaces/IMyShipments'
import { EXPRESS_SLA_ID, IAvailableWindowWithExpress } from '../../types'

const getOperationalDateTime = (startDateUtc?: string): string => {
  return startDateUtc?.slice(0, 19) ?? ''
}

/**
 * Matches available windows with corresponding SLA IDs, prioritizing "Despacho a Domicilio"
 *
 * @param {IAvailableWindows[]} availableWindows - List of available delivery windows
 * @param {IAvailableSlas} availableSlas - List of available SLAs
 * @returns {IAvailableWindows[]} Windows with matched SLA IDs
 *
 * @example
 * const windows = [
 *   { startDateUtc: '2025-01-23T11:30:00+00:00', price: 2990 }
 * ];
 *
 * const slas = [
 *   {
 *     id: 'Despacho a Domicilio',
 *     availableDeliveryWindows: [
 *       { startDateUtc: '2025-01-23T11:30:00+00:00' }
 *     ]
 *   }
 * ];
 *
 * const result = matchWindowsToSla(windows, slas);
 * // result: [{ startDateUtc: '2025-01-23T11:30:00+00:00', price: 2990, id: 'Despacho a Domicilio' }]
 */
export const matchWindowsToSla = (
  availableWindows: IAvailableWindows[],
  availableSlas: IAvailableSlas,
  expressMetadata?: Pick<
    IAvailableWindowWithExpress,
    'expressAmount' | 'expressExceed'
  >
): IAvailableWindowWithExpress[] => {
  const windowsWithSla = availableWindows.map((window) => {
    // First, prioritize "Despacho a Domicilio"
    const despachoSla = availableSlas.find((sla) => {
      return (
        sla.id === 'Despacho a Domicilio' &&
        sla.availableDeliveryWindows.some((dw) => {
          return dw.startDateUtc === window.startDateUtc
        })
      )
    })

    if (despachoSla) {
      return { ...window, id: despachoSla?.id }
    }

    // If no "Despacho a Domicilio", find first matching SLA
    const matchingSla = availableSlas.find((sla) => {
      return sla.availableDeliveryWindows.some((dw) => {
        return dw.startDateUtc === window.startDateUtc
      })
    })

    return {
      ...window,
      id: matchingSla?.id ?? window?.id
    }
  })

  const windowKeys = windowsWithSla.reduce<Record<string, number>>(
    (keys, window, index) => {
      keys[
        `${window?.id ?? ''}-${window?.startDateUtc}-${window?.endDateUtc}`
      ] = index
      return keys
    },
    {}
  )

  availableSlas.forEach((sla) => {
    const availableDeliveryWindows =
      sla.availableDeliveryWindows as IAvailableWindowWithExpress[]

    availableDeliveryWindows.forEach((window) => {
      const windowWithSla = {
        ...window,
        id: sla.id === EXPRESS_SLA_ID ? sla.id : window?.id ?? sla.id
      }
      const windowKey = `${
        windowWithSla?.id ?? ''
      }-${windowWithSla?.startDateUtc}-${windowWithSla?.endDateUtc}`

      const existingWindowIndex = windowKeys[windowKey]

      if (existingWindowIndex === undefined) {
        windowsWithSla.push(windowWithSla)
        windowKeys[windowKey] = windowsWithSla.length - 1
      } else {
        const existingWindow = windowsWithSla[
          existingWindowIndex
        ] as IAvailableWindowWithExpress
        const mergedWindow = {
          ...existingWindow,
          ...windowWithSla
        }

        if (
          existingWindow?.expressAmount !== undefined ||
          windowWithSla?.expressAmount !== undefined
        ) {
          mergedWindow.expressAmount =
            windowWithSla?.expressAmount ?? existingWindow?.expressAmount
        }

        if (
          existingWindow?.expressExceed !== undefined ||
          windowWithSla?.expressExceed !== undefined
        ) {
          mergedWindow.expressExceed =
            Boolean(existingWindow?.expressExceed) ||
            Boolean(windowWithSla?.expressExceed)
        }

        windowsWithSla[existingWindowIndex] = mergedWindow
      }
    })
  })

  const windowsWithExpressMetadata = windowsWithSla.map((window) => {
    const windowWithExpress = window as IAvailableWindowWithExpress

    if (windowWithExpress?.id !== EXPRESS_SLA_ID) return windowWithExpress

    return {
      ...windowWithExpress,
      ...(windowWithExpress?.expressAmount === undefined &&
      expressMetadata?.expressAmount !== undefined
        ? { expressAmount: expressMetadata.expressAmount }
        : {}),
      ...(windowWithExpress?.expressExceed === undefined &&
      expressMetadata?.expressExceed !== undefined
        ? { expressExceed: expressMetadata.expressExceed }
        : {})
    }
  })

  return windowsWithExpressMetadata.sort((firstWindow, secondWindow) => {
    return getOperationalDateTime(firstWindow?.startDateUtc).localeCompare(
      getOperationalDateTime(secondWindow?.startDateUtc)
    )
  })
}
